import { FC, useMemo } from 'react';
import { DecimalFormat } from '@tempusfinance/decimal';
import { Decimal } from '../../../../datastructures';
import { Nullable } from '../../../../interfaces';
import { HEALTH_FACTOR_DANGER, HEALTH_FACTOR_INFINITY, HEALTH_FACTOR_WARNING } from '../../../../constants';
import Typography, { TypographyColor, TypographyVariant } from '../../../shared/Typography';

interface LendAndBorrowHealthFactorLabelProps {
  healthFactor: Nullable<Decimal>;
  size?: 'normal' | 'medium' | 'large';
}

const LendAndBorrowHealthFactorLabel: FC<LendAndBorrowHealthFactorLabelProps> = ({ healthFactor, size = 'normal' }) => {
  const healthFactorFormatted = useMemo(() => {
    if (!healthFactor) {
      return null;
    }

    if (healthFactor.gte(HEALTH_FACTOR_INFINITY)) {
      return 'Infinite';
    }

    return DecimalFormat.format(healthFactor, {
      style: 'decimal',
      fractionDigits: 2,
      pad: true,
    });
  }, [healthFactor]);

  const healthFactorColor: TypographyColor = useMemo(() => {
    if (!healthFactor) {
      return 'text-primary';
    }

    if (healthFactor.lt(HEALTH_FACTOR_DANGER)) {
      return 'text-error';
    }

    if (healthFactor.lte(HEALTH_FACTOR_WARNING)) {
      return 'text-warning';
    }

    return 'text-success';
  }, [healthFactor]);

  const healthFactorLabelSize: TypographyVariant = useMemo(() => {
    if (size === 'normal') {
      return 'table-body-sm';
    }

    if (size === 'large') {
      return 'heading-xs';
    }

    if (size === 'medium') {
      return 'button-sm';
    }

    return 'table-body-sm';
  }, [size]);

  return (
    <Typography variant={healthFactorLabelSize} weight="semi-bold" color={healthFactorColor}>
      {healthFactorFormatted}
    </Typography>
  );
};
export default LendAndBorrowHealthFactorLabel;
